import { useEffect, useMemo, useState } from "react";
import SearchInput from "../searchInput/SearchInput";
import "../checkbox/Checkbox.css";

export default function DeeplinkInstitutionPicker({
  institutions = [],
  selectedIds = [],
  onChange,
  isOpen = true,
}) {
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (isOpen) setSearch("");
  }, [isOpen]);

  const sortedInstitutions = useMemo(
    () =>
      [...institutions].sort((a, b) =>
        (a.institutionName || "").localeCompare(b.institutionName || "")
      ),
    [institutions]
  );

  const filteredInstitutions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sortedInstitutions;
    return sortedInstitutions.filter((institution) => {
      const name = (institution.institutionName || "").toLowerCase();
      const id = (institution.institutionId || "").toLowerCase();
      return name.includes(term) || id.includes(term);
    });
  }, [sortedInstitutions, search]);

  const toggleInstitution = (institutionId) => {
    const next = selectedIds.includes(institutionId)
      ? selectedIds.filter((id) => id !== institutionId)
      : [...selectedIds, institutionId];
    onChange(next);
  };

  const selectAllFiltered = () => {
    const merged = new Set([
      ...selectedIds,
      ...filteredInstitutions.map((institution) => institution.institutionId),
    ]);
    onChange([...merged]);
  };

  const clearAll = () => onChange([]);

  const hasFilteredResults = filteredInstitutions.length > 0;
  const hasSelection = selectedIds.length > 0;
  const allFilteredSelected =
    hasFilteredResults &&
    filteredInstitutions.every((institution) =>
      selectedIds.includes(institution.institutionId)
    );

  return (
    <div className="deeplink-modal-institutions">
      {institutions.length === 0 ? (
        <span className="deeplink-modal-institutions-empty">
          No institutions available
        </span>
      ) : (
        <>
          <div className="deeplink-modal-institutions-search">
            <SearchInput
              containerWidth="100%"
              text="Search by name or ID..."
              value={search}
              onChangeText={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="deeplink-modal-institutions-toolbar">
            <span className="deeplink-modal-institutions-count">
              {hasSelection
                ? `${selectedIds.length} selected`
                : "None selected"}
            </span>
            <div className="deeplink-modal-institutions-actions">
              {hasFilteredResults && !allFilteredSelected ? (
                <button
                  type="button"
                  className="deeplink-modal-institutions-action"
                  onClick={selectAllFiltered}
                >
                  {search.trim() ? "Select matching" : "Select all"}
                </button>
              ) : null}
              {hasSelection ? (
                <button
                  type="button"
                  className="deeplink-modal-institutions-action"
                  onClick={clearAll}
                >
                  Clear all
                </button>
              ) : null}
            </div>
          </div>

          <div className="deeplink-modal-institutions-list">
            {filteredInstitutions.length === 0 ? (
              <span className="deeplink-modal-institutions-empty">
                No institutions match your search
              </span>
            ) : (
              filteredInstitutions.map((institution) => {
                const institutionId = institution.institutionId;
                const isChecked = selectedIds.includes(institutionId);

                return (
                  <div
                    key={institutionId}
                    className="checkbox-wrapper deeplink-institution-row"
                  >
                    <input
                      id={`deeplink-institution-${institutionId}`}
                      type="checkbox"
                      value={institutionId}
                      checked={isChecked}
                      onChange={() => toggleInstitution(institutionId)}
                    />
                    <label
                      htmlFor={`deeplink-institution-${institutionId}`}
                      className="form-control deeplink-institution-label"
                    >
                      <span className="deeplink-institution-name">
                        {institution.institutionName || institutionId}
                      </span>
                      {institution.institutionName ? (
                        <span className="deeplink-institution-id">
                          {institutionId}
                        </span>
                      ) : null}
                    </label>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}
